import { useCallback } from 'react';
import { useTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { LIST_ITEM_TOP_MARGIN } from '../../List';
import { mod } from '../../utils/func';
import { DROPDOWN_ITEM_ROLE } from './useMenuItems';

export const DROPDOWN_MENU_ROLE = 'menu';

export const useKeyboardNavigation = (
    itemsRef: React.MutableRefObject<(HTMLDivElement | null)[]>,
    opened: boolean,
    open: (props: { getFocusItem: () => HTMLDivElement | null }) => void,
    close: () => void,
    footerRef?: React.RefObject<HTMLDivElement>,
    setActiveItemIndex?: (index: number) => void
) => {
    const theme = useTheme();
    const getSelectableItems = useCallback(
        () => itemsRef.current.filter((item) => item && item.getAttribute('role') === DROPDOWN_ITEM_ROLE),
        [itemsRef]
    );

    const getFirstItem = useCallback(() => {
        const selectableItems = getSelectableItems();
        return selectableItems[0];
    }, [getSelectableItems]);

    const getLastItem = useCallback(() => {
        const selectableItems = getSelectableItems();
        return selectableItems.at(-1) ?? null;
    }, [getSelectableItems]);

    const moveItemFocus = useCallback(
        ({ indexDiff }: { indexDiff: number }) => {
            const selectableItems = getSelectableItems();
            const activeIndex = selectableItems.indexOf(document.activeElement as HTMLDivElement);
            const nextIndex = mod(activeIndex + indexDiff, selectableItems.length);
            const nextItem = selectableItems[nextIndex];

            if (nextItem) {
                nextItem.focus();
                setActiveItemIndex?.(nextIndex);

                // Scroll into view with footer offset
                const footerHeight = footerRef?.current?.offsetHeight ?? 0;
                const menuContainer = nextItem.closest(`[role="${DROPDOWN_MENU_ROLE}"]`);
                if (menuContainer) {
                    const itemBottom = nextItem.offsetTop + nextItem.offsetHeight;
                    const visibleHeight = menuContainer.clientHeight - footerHeight;

                    if (itemBottom > visibleHeight) {
                        menuContainer.scrollTop = itemBottom - visibleHeight + LIST_ITEM_TOP_MARGIN({ theme });
                    }
                }
            }
        },
        [getSelectableItems, setActiveItemIndex, footerRef, theme]
    );

    const handleMenuButtonKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            let handled = false;
            const key = event.key === ' ' ? KeyValue.Spacebar : event.key;
            switch (key) {
                case KeyValue.Enter:
                case KeyValue.Spacebar:
                case KeyValue.ArrowDown:
                    if (!opened) {
                        open({ getFocusItem: getFirstItem });
                        handled = true;
                    }
                    break;

                case KeyValue.ArrowUp:
                    if (!opened) {
                        open({ getFocusItem: getLastItem });
                        handled = true;
                    }
                    break;

                default:
                    break;
            }

            if (handled) {
                event.stopPropagation();
                event.preventDefault();
            }
        },
        [opened, open, getLastItem, getFirstItem]
    );

    const handleMenuKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            let handled = false;
            const key = event.key === ' ' ? KeyValue.Spacebar : event.key;
            switch (key) {
                case KeyValue.ArrowDown:
                    moveItemFocus({ indexDiff: 1 });
                    handled = true;
                    break;

                case KeyValue.ArrowUp:
                    moveItemFocus({ indexDiff: -1 });
                    handled = true;
                    break;

                case KeyValue.Home:
                case KeyValue.PageUp: {
                    const firstItem = getFirstItem();
                    if (firstItem) {
                        firstItem.focus();
                        handled = true;
                    }
                    break;
                }

                case KeyValue.End:
                case KeyValue.PageDown: {
                    const lastItem = getLastItem();
                    if (lastItem) {
                        lastItem.focus();
                        handled = true;
                    }
                    break;
                }

                case KeyValue.Enter:
                case KeyValue.Spacebar:
                    if (document.activeElement) {
                        (document.activeElement as HTMLElement).click();
                    }
                    handled = true;
                    break;

                case KeyValue.Escape:
                    close();
                    handled = true;
                    break;

                case KeyValue.Tab:
                    if (!footerRef?.current) {
                        close();
                    }
                    break;

                default:
                    break;
            }

            if (handled) {
                event.stopPropagation();
                event.preventDefault();
            }
        },
        [moveItemFocus, getFirstItem, getLastItem, close, footerRef]
    );

    return {
        getFirstItem,
        getLastItem,
        handleMenuButtonKeyDown,
        handleMenuKeyDown,
    };
};
